import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Download, Share2, Link2, FilePlus2, Ban, History } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { Button } from '@/components/ui/Button';

/*
 * PR-TRIP-AUDIT Step 8 — the unreported trips queue.
 * Each row is a leg GPS recorded between two real sites, on a lane the records
 * show as billable, with no matching row in the operations Excel sheets.
 *
 * Three actions, all logged with who did what and why:
 *   Link        the trip is already in the records and the match was missed
 *   Create      a record has to be raised for it
 *   Not a trip  the leg is not billable; a reason is required
 *
 * Anything still open after three days turns red.
 */

interface Row {
  id: string; plate: string; from_terminal: string; to_terminal: string;
  departed_at: string; arrived_at: string; km: number | null;
  lane_vendor: string | null; lane_item: string | null; lane_rate: number | null;
  est_value_sar: number | null; status: string; resolution: string | null;
  first_seen_at: string; resolved_at: string | null; resolved_by_name: string | null;
  age_days: number;
}
interface Resp { rows: Row[]; openCount: number; openValue: number; overdueCount: number }

type Status = 'open' | 'linked' | 'created' | 'not_a_trip' | 'all';

const niceDay = (s: string) =>
  new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
const hhmm = (s: string) => s.slice(11, 16);
const sar = (n: number | null) => (n == null ? '—' : `SAR ${Math.round(n).toLocaleString('en-US')}`);

const LABEL: Record<string, { text: string; cls: string }> = {
  open:       { text: 'Open',       cls: 'border-amber-500/40 text-amber-600' },
  linked:     { text: 'Linked',     cls: 'border-emerald-500/40 text-emerald-600' },
  created:    { text: 'Created',    cls: 'border-emerald-500/40 text-emerald-600' },
  not_a_trip: { text: 'Not a trip', cls: 'border-border text-muted-foreground' },
};

export default function UnreportedTripsPage() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<Status>('open');
  const [asking, setAsking] = useState<{ id: string; action: 'linked' | 'created' | 'not_a_trip' } | null>(null);
  const [note, setNote] = useState('');
  const [shared, setShared] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['unreported', status],
    queryFn: async () => (await apiClient.get<Resp>('/api/v1/vehicle-history/unreported',
      { params: { status } })).data,
  });

  const resolve = useMutation({
    mutationFn: async (v: { id: string; action: string; note?: string }) =>
      apiClient.post(`/api/v1/vehicle-history/unreported/${v.id}/resolve`,
        { action: v.action, note: v.note || undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['unreported'] });
      setAsking(null); setNote('');
    },
  });

  const rows = data?.rows ?? [];
  // The queue is open-ended, so the download covers a wide window on purpose.
  const span = { from: '2026-01-01', to: new Date().toISOString().slice(0, 10) };

  const dl = async (format: 'xlsx' | 'pdf') => {
    setBusy(format);
    try {
      const res = await apiClient.get('/api/v1/vehicle-history/report',
        { params: { kind: 'unreported', format, ...span }, responseType: 'blob' });
      const cd = String(res.headers['content-disposition'] ?? '');
      const name = /filename="([^"]+)"/.exec(cd)?.[1] ?? `unreported-trips.${format}`;
      const href = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = href; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 4000);
    } finally { setBusy(null); }
  };

  const share = async () => {
    setBusy('share');
    try {
      const res = await apiClient.post<{ path: string }>('/api/v1/vehicle-history/share',
        { kind: 'unreported', ...span });
      const url = `${window.location.origin}${res.data.path}`;
      setShared(url);
      try { await navigator.clipboard.writeText(url); } catch { /* clipboard blocked */ }
    } finally { setBusy(null); }
  };

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      <header className="border-b border-border bg-card sticky top-0 z-20">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center gap-2 justify-between">
          <div className="font-display text-lg">Unreported trips</div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="gap-2"
              onClick={() => { window.location.href = '/dashboard/vehicle-history'; }}>
              <History className="h-4 w-4" /> Vehicle history
            </Button>
            <Button variant="ghost" size="sm" className="gap-2"
              onClick={() => { window.location.href = '/dashboard'; }}>
              <ArrowLeft className="h-4 w-4" /> Dashboard
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-[1400px] mx-auto w-full px-4 sm:px-6 py-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          {[
            ['Open', String(data?.openCount ?? 0), ''],
            ['Estimated value', sar(data?.openValue ?? 0), ''],
            ['Open 3 days or more', String(data?.overdueCount ?? 0),
              data?.overdueCount ? 'text-rose-600' : 'text-muted-foreground'],
            ['Showing', String(rows.length), ''],
          ].map(([l, v, c], i) => (
            <div key={i} className="rounded-xl border border-border bg-card p-4 min-w-0">
              <div className="text-xs text-muted-foreground">{l}</div>
              <div className={`text-2xl font-semibold mt-1 ${c}`}>{v}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-3">
          <select value={status} onChange={(e) => setStatus(e.target.value as Status)}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm">
            <option value="open">Open</option>
            <option value="linked">Linked</option>
            <option value="created">Record created</option>
            <option value="not_a_trip">Not a trip</option>
            <option value="all">All</option>
          </select>
          <Button variant="outline" size="sm" className="gap-2" disabled={busy === 'xlsx'}
            onClick={() => dl('xlsx')}><Download className="h-4 w-4" /> Excel</Button>
          <Button variant="outline" size="sm" className="gap-2" disabled={busy === 'pdf'}
            onClick={() => dl('pdf')}><Download className="h-4 w-4" /> PDF</Button>
          <Button variant="gold" size="sm" className="gap-2" disabled={busy === 'share'}
            onClick={share}><Share2 className="h-4 w-4" /> Share link</Button>
        </div>

        {shared && (
          <div className="mb-3 rounded-md border border-border bg-muted/40 p-2 text-xs break-all">
            Copied. Read-only, works without a login, expires in 7 days:
            <span className="font-mono ms-1">{shared}</span>
          </div>
        )}

        <div className="text-sm text-muted-foreground mb-3">
          Vendor, item and rate are the most common values on the same lane in your own records, so
          the value is an estimate, not an invoice.
        </div>

        <div className="rounded-xl border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="text-start font-medium px-3 py-2">Plate</th>
                <th className="text-start font-medium px-3 py-2">Departed</th>
                <th className="text-start font-medium px-3 py-2">Route</th>
                <th className="text-end font-medium px-3 py-2">Km</th>
                <th className="text-start font-medium px-3 py-2">Lane in records</th>
                <th className="text-end font-medium px-3 py-2">Estimate</th>
                <th className="text-start font-medium px-3 py-2">State</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const red = r.status === 'open' && r.age_days >= 3;
                return (
                  <tr key={r.id} className={`border-t border-border ${red ? 'bg-rose-500/5' : ''}`}>
                    <td className="px-3 py-2 font-medium whitespace-nowrap">{r.plate}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {niceDay(r.departed_at)}
                      <span className="text-muted-foreground ms-1">{hhmm(r.departed_at)}</span>
                    </td>
                    <td className="px-3 py-2">{r.from_terminal} → {r.to_terminal}</td>
                    <td className="px-3 py-2 text-end tabular-nums">{r.km ?? '—'}</td>
                    <td className="px-3 py-2">
                      {r.lane_vendor ?? <span className="text-muted-foreground">no lane history</span>}
                      {r.lane_item && <div className="text-xs text-muted-foreground">
                        {r.lane_item}{r.lane_rate != null ? ` · ${r.lane_rate.toFixed(2)}` : ''}
                      </div>}
                    </td>
                    <td className="px-3 py-2 text-end tabular-nums">{sar(r.est_value_sar)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`inline-block rounded border px-1.5 py-0.5 text-xs ${LABEL[r.status]?.cls ?? ''}`}>
                        {LABEL[r.status]?.text ?? r.status}
                      </span>
                      <div className={`text-xs mt-0.5 ${red ? 'text-rose-600 font-medium' : 'text-muted-foreground'}`}>
                        {r.status === 'open' ? `${r.age_days}d open` : (r.resolved_by_name ?? '')}
                      </div>
                      {r.status !== 'open' && r.resolution && (
                        <div className="text-xs text-muted-foreground max-w-[200px] truncate" title={r.resolution}>
                          {r.resolution}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {r.status === 'open' && (
                        <div className="flex flex-wrap gap-1.5 justify-end">
                          <Button size="sm" variant="outline" className="gap-1"
                            onClick={() => { setAsking({ id: r.id, action: 'linked' }); setNote(''); }}>
                            <Link2 className="h-3.5 w-3.5" /> Link
                          </Button>
                          <Button size="sm" variant="outline" className="gap-1"
                            onClick={() => { setAsking({ id: r.id, action: 'created' }); setNote(''); }}>
                            <FilePlus2 className="h-3.5 w-3.5" /> Create
                          </Button>
                          <Button size="sm" variant="ghost" className="gap-1 text-muted-foreground"
                            onClick={() => { setAsking({ id: r.id, action: 'not_a_trip' }); setNote(''); }}>
                            <Ban className="h-3.5 w-3.5" /> Not a trip
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!isLoading && !rows.length && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">
                  Nothing here. Every GPS trip on a billable lane has a record.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </main>

      {/* ---- confirm, with a mandatory reason for "not a trip" ------------ */}
      {asking && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4"
          onClick={() => setAsking(null)}>
          <div className="w-full max-w-md rounded-xl border border-border bg-card p-4"
            onClick={(e) => e.stopPropagation()}>
            <div className="font-medium mb-1">
              {asking.action === 'linked' && 'Link to an existing record'}
              {asking.action === 'created' && 'Mark a record as created'}
              {asking.action === 'not_a_trip' && 'Mark as not a trip'}
            </div>
            <div className="text-sm text-muted-foreground mb-3">
              {asking.action === 'not_a_trip'
                ? 'Say why this leg is not billable. The reason is kept with your name.'
                : 'Add a note if it helps the next person. Your name and the time are recorded either way.'}
            </div>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={500}
              placeholder={asking.action === 'not_a_trip'
                ? 'Reason, e.g. empty repositioning move, no load carried'
                : asking.action === 'linked' ? 'Which record, e.g. Sep sheet row 214' : 'Optional note'}
              className="w-full rounded-md border border-input bg-background p-2 text-sm mb-3" />
            {resolve.isError && (
              <div className="text-sm text-rose-600 mb-2">
                {(resolve.error as { response?: { data?: { error?: string } } })?.response?.data?.error
                  === 'reason_required' ? 'A reason is required.' : 'That did not save. Try again.'}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setAsking(null)}>Cancel</Button>
              <Button size="sm"
                disabled={resolve.isPending || (asking.action === 'not_a_trip' && !note.trim())}
                onClick={() => resolve.mutate({ id: asking.id, action: asking.action, note })}>
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
